import { useMemo } from 'react';
import { Country } from '@tiendanube/common/src/enums';
import ShowByCountry from 'App/components/ShowByCountry';
import { FEATURE_METAFIELDS_CUSTOMER } from 'App/features';
import { B2B_WHOLESALE } from 'App/featuresFlags';
import HelpLink from 'App/HelpLink';
import { useHasTags, useNavegate } from 'App/hooks';
import { HELP_LINKS_CUSTOMER_FILTERS } from 'commons/constants';
import { useTranslationLanguage } from 'commons/hooks';
import { CUSTOMERS_SHOW_FILTERS } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import MetafieldEmptyState from 'domains/Customers/Customers/pages/CustomerListPage/components/FilterModal/components/MetafieldEmptyState';
import ModalButtons from 'domains/Customers/Customers/pages/CustomerListPage/components/FilterModal/components/ModalButtons';
import WholesaleFilter from 'domains/Customers/Customers/pages/CustomerListPage/components/FilterModal/components/WholesaleFilter';
import { useTranslationCustomers } from 'domains/Customers/hooks';
import { MetafieldsFilters } from 'domains/Metafields/components';
import { useGetCustomerMetafields } from 'domains/Metafields/hooks';

export default function ModalContent({
  values,
  selecteds,
  handleChange,
  handleChangeMetafields,
  handleApply,
  handleReset,
}) {
  const t = useTranslationCustomers();
  const {
    hasInternals,
    hasExternals,
    status,
    metafields,
    apiMetafields,
    fetchMetafields,
  } = useGetCustomerMetafields();
  const { goTo } = useNavegate();
  const language = useTranslationLanguage();

  const [hasFilterWholesaleTag] = useHasTags(B2B_WHOLESALE);

  const allMetafields = useMemo(
    () =>
      [...(metafields ?? []), ...(apiMetafields ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [metafields, apiMetafields],
  );

  const handleCreateMetafield = useUpsellFlow({
    title: t('customerListPage.upsell.filters'),
    featureKey: FEATURE_METAFIELDS_CUSTOMER,
    trackingSource: CUSTOMERS_SHOW_FILTERS,
    asAside: true,
    callback: () => goTo('/settings/metafields'),
  });

  const handleApplyWithUpsell = useUpsellFlow({
    title: t('customerListPage.upsell.filters'),
    featureKey: FEATURE_METAFIELDS_CUSTOMER,
    trackingSource: CUSTOMERS_SHOW_FILTERS,
    asAside: true,
    callback: handleApply,
  });

  const handleChangeMetafieldUpsell = useUpsellFlow({
    title: t('customerListPage.upsell.filters'),
    featureKey: FEATURE_METAFIELDS_CUSTOMER,
    trackingSource: CUSTOMERS_SHOW_FILTERS,
    callback: handleChangeMetafields,
    asAside: true,
  });

  const hasMetafields = hasInternals || hasExternals;

  if (hasFilterWholesaleTag && !hasMetafields) {
    return (
      <WholesaleFilter
        value={`${values.hasPriceTable}`}
        onChange={({ value }) => handleChange({ name: 'hasPriceTable', value })}
      />
    );
  }

  if (hasMetafields) {
    return (
      <>
        {hasFilterWholesaleTag && (
          <WholesaleFilter
            value={`${values.hasPriceTable}`}
            onChange={({ value }) =>
              handleChange({ name: 'hasPriceTable', value })
            }
          />
        )}

        <MetafieldsFilters
          metafields={allMetafields || []}
          status={status}
          selecteds={selecteds}
          onChange={
            hasFilterWholesaleTag
              ? handleChangeMetafieldUpsell
              : handleChangeMetafields
          }
          onFetch={fetchMetafields}
        />

        <ModalButtons
          handleReset={handleReset}
          handleApply={
            hasFilterWholesaleTag ? handleApply : handleApplyWithUpsell
          }
        />
      </>
    );
  }

  return (
    <>
      <MetafieldEmptyState handleCreateMetafield={handleCreateMetafield} />

      <ShowByCountry allowedCountries={[Country.BR]}>
        <HelpLink
          title={t('customerListPage.filterModal.helpLink.title')}
          previousValue=""
          currentViewTracking=""
          linkURL={HELP_LINKS_CUSTOMER_FILTERS[language]}
          icon="both"
        />
      </ShowByCountry>
    </>
  );
}
